/**
 * 交換QRの明細の価格の導出
 * 必要ptは窓口の確定時点の値で決まり、QRは価格を持ちません。そのため単価・小計・合計は保存せず、描画のたびに最新の景品カタログから導きます。
 */

/** 価格を引く景品カタログ */
export type ExchangeQrCatalog = {
  rewards: ReadonlyArray<{ rewardId: string; costPoints: number }>;
  rewardSets: ReadonlyArray<{ rewardSetId: string; totalCostPoints: number }>;
};

/** 交換QRの明細（保存する形） */
export type ExchangeQrStoredItem = {
  /** `single:<景品ID>` / `set:<セットID>`。注文の明細は先頭に `order:` が付く */
  key: string;
  itemName: string;
  quantity: number;
  /** セットの明細なら景品セットID */
  rewardSetId?: string | null;
  /** QRに付けた注文の明細か（無ければ申請分） */
  isOrder?: boolean;
};

/** 価格を導いた明細 */
export type ExchangeQrPricedItem<T> = T & {
  /** 単価。カタログに無ければnull */
  unitPoints: number | null;
  /** 小計。カタログに無ければnull */
  subtotalPoints: number | null;
};

/** 注文の明細のキーの接頭辞 */
const ORDER_KEY_PREFIX = 'order:';

/**
 * 明細がQRに付けた注文か判定する。isOrder の無い旧形式はキーで分かる
 * @param {{ key?: unknown, isOrder?: boolean }} item - 明細
 * @returns {boolean} 注文の明細ならtrue
 */
export const isExchangeQrOrderItem = (item: { key?: unknown; isOrder?: boolean }): boolean => (
  item.isOrder === true || (typeof item.key === 'string' && item.key.startsWith(ORDER_KEY_PREFIX))
);

/**
 * 明細のキーから、最新のカタログの単価を引く
 * @param {unknown} key - 明細のキー
 * @param {ExchangeQrCatalog} catalog - 最新の景品カタログ
 * @returns {number | null} 単価。カタログに無い・キーが読めない場合はnull
 */
export const resolveExchangeQrItemUnitPoints = (key: unknown, catalog: ExchangeQrCatalog): number | null => {
  if (typeof key !== 'string') return null;
  /** 注文の接頭辞を除いたキー */
  const itemKey = key.startsWith(ORDER_KEY_PREFIX) ? key.slice(ORDER_KEY_PREFIX.length) : key;
  /** 種別とIDの区切り */
  const separatorIndex = itemKey.indexOf(':');
  /** 種別 */
  const requestType = itemKey.slice(0, separatorIndex);
  /** 景品またはセットのID */
  const id = itemKey.slice(separatorIndex + 1);
  if (requestType === 'single') {
    return catalog.rewards.find((reward) => reward.rewardId === id)?.costPoints ?? null;
  }
  if (requestType === 'set') {
    return catalog.rewardSets.find((rewardSet) => rewardSet.rewardSetId === id)?.totalCostPoints ?? null;
  }
  return null;
};

/**
 * 交換QRの明細の単価・小計・合計を導く
 * 合計は「申請中のサーバー値 + 注文の明細の小計」。申請分はカタログに無い非公開の景品も含むため、サーバー値を使う。
 * @template T
 * @param {Object} params - 入力
 * @param {ReadonlyArray<T> | null | undefined} params.items - 明細
 * @param {ExchangeQrCatalog} params.catalog - 最新の景品カタログ
 * @param {number | null} params.requestedPointTotal - 申請中の合計pt。分からない場合はnull
 * @returns {{ items: Array<ExchangeQrPricedItem<T>>, totalQuantity: number, totalPoints: number | null }} 導いた明細と合計。価格の分からない注文があれば合計はnull
 */
export const deriveExchangeQrPricing = <T extends { key: string; quantity: number; isOrder?: boolean }>({
  items,
  catalog,
  requestedPointTotal,
}: {
  items: ReadonlyArray<T> | null | undefined;
  catalog: ExchangeQrCatalog;
  requestedPointTotal: number | null;
}): { items: Array<ExchangeQrPricedItem<T>>; totalQuantity: number; totalPoints: number | null } => {
  /** 価格を導いた明細。古い形式の保存値はここで上書きする */
  const pricedItems = (Array.isArray(items) ? items : []).map((item): ExchangeQrPricedItem<T> => {
    /** 単価 */
    const unitPoints = resolveExchangeQrItemUnitPoints(item.key, catalog);
    return { ...item, unitPoints, subtotalPoints: unitPoints === null ? null : unitPoints * item.quantity };
  });
  /** 注文の明細 */
  const orderItems = pricedItems.filter(isExchangeQrOrderItem);
  /** 合計が分かるか */
  const canTotal = requestedPointTotal !== null && orderItems.every((item) => item.subtotalPoints !== null);
  return {
    items: pricedItems,
    totalQuantity: pricedItems.reduce((total, item) => total + item.quantity, 0),
    totalPoints: canTotal
      ? orderItems.reduce((total, item) => total + (item.subtotalPoints ?? 0), requestedPointTotal)
      : null,
  };
};

/**
 * 明細を保存する形にする（単価・小計を保存しない）
 * @param {ExchangeQrStoredItem} item - 明細
 * @returns {ExchangeQrStoredItem} 保存する明細
 */
export const toStoredExchangeQrItem = ({
  key,
  itemName,
  quantity,
  rewardSetId,
  isOrder,
}: ExchangeQrStoredItem): ExchangeQrStoredItem => ({
  key,
  itemName,
  quantity,
  ...(rewardSetId ? { rewardSetId } : {}),
  ...(isOrder ? { isOrder } : {}),
});
